// [phase12-backtest] Signal backtest summary card — trading-ui.
//
// Read-only display of the Phase 12 deterministic historical replay.
// Honest-empty: shows pending/unavailable copy when the summary file is
// missing. NEVER shows fake ML / probability / accuracy claims. The
// banner text "Historical paper replay only — not a live trading result."
// is preserved verbatim per the Phase 12 work order.
import { BarChart3 } from "lucide-react";
import type { SignalBacktestSummary as SignalBacktestSummaryT } from "../../lib/signalClient";

interface Props {
  data: SignalBacktestSummaryT | null;
  loading: boolean;
  error: string | null;
}

function badgeClasses(tone: "green" | "amber" | "red" | "blue" | "neutral"): string {
  const base =
    "inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-black tracking-widest uppercase border";
  switch (tone) {
    case "green":
      return `${base} bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30`;
    case "amber":
      return `${base} bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30`;
    case "red":
      return `${base} bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30`;
    case "blue":
      return `${base} bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30`;
    default:
      return `${base} bg-[var(--card-bg)] text-[var(--muted-text)] border-[var(--card-border)]`;
  }
}

function formatPct(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  return `${(v * 100).toFixed(1)}%`;
}

function formatPnlPct(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  const sign = v >= 0 ? "+" : "";
  return `${sign}${v.toFixed(2)}%`;
}

function MetricTile({
  label, value, tone = "neutral",
}: {
  label: string;
  value: string;
  tone?: "green" | "amber" | "red" | "blue" | "neutral";
}) {
  const toneClass =
    tone === "green" ? "text-emerald-500"
    : tone === "amber" ? "text-amber-500"
    : tone === "red" ? "text-red-500"
    : tone === "blue" ? "text-blue-500"
    : "text-[var(--app-text)]";
  return (
    <div className="rounded-lg border border-[var(--card-border)] bg-[var(--card-bg)] p-3">
      <div className="text-[9px] uppercase tracking-wider text-[var(--muted-text)] font-bold mb-1">
        {label}
      </div>
      <div className={`text-lg font-black font-mono ${toneClass}`}>{value}</div>
    </div>
  );
}

export default function SignalBacktestSummary({ data, loading, error }: Props) {
  if (loading && !data) {
    return (
      <section
        data-testid="signal-backtest-summary"
        data-state="loading"
        className="rounded-2xl border border-[var(--card-border)] bg-[var(--card-bg)] p-4"
      >
        <div className="flex items-center gap-2 text-sm font-bold tracking-wider uppercase text-[var(--app-text)]">
          <BarChart3 className="h-4 w-4" /> Backtest Summary · phase12-backtest
        </div>
        <div className="text-xs text-[var(--muted-text)] mt-2">Loading Phase 12 backtest summary…</div>
      </section>
    );
  }

  if (error || !data) {
    return (
      <section
        data-testid="signal-backtest-summary"
        data-state="error"
        className="rounded-2xl border border-[var(--card-border)] bg-[var(--card-bg)] p-4"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm font-bold tracking-wider uppercase text-[var(--app-text)]">
            <BarChart3 className="h-4 w-4" /> Backtest Summary · phase12-backtest
          </div>
          <span className={badgeClasses("amber")}>UNAVAILABLE</span>
        </div>
        <div className="text-xs text-[var(--muted-text)] mt-2">
          {error || "No backtest summary returned."}
        </div>
        <div className="text-[9px] text-[var(--muted-text)] italic mt-1">
          Historical paper replay only — not a live trading result.
        </div>
      </section>
    );
  }

  const isPending = data.status === "PENDING" || data.status === "LABELS_MISSING" ||
    data.status === "CANDIDATES_MISSING" || data.status === "EMPTY";

  if (isPending) {
    return (
      <section
        data-testid="signal-backtest-summary"
        data-state={data.status}
        className="rounded-2xl border border-[var(--card-border)] bg-[var(--card-bg)] p-4"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-sm font-bold tracking-wider uppercase text-[var(--app-text)]">
            <BarChart3 className="h-4 w-4" /> Backtest Summary · phase12-backtest
          </div>
          <span className={badgeClasses("amber")}>{data.status}</span>
        </div>
        <div className="text-xs text-[var(--muted-text)] mt-2 leading-relaxed">
          {data.status === "PENDING" && (
            <>No backtest on disk for {data.date} yet — the daily replay runs at 11:15 ET after labels finish.</>
          )}
          {data.status === "LABELS_MISSING" && (
            <>Labels for {data.date} are not yet on disk; the replay will run once labels complete.</>
          )}
          {data.status === "CANDIDATES_MISSING" && (
            <>No candidate set existed for {data.date}, so there is nothing to replay.</>
          )}
          {data.status === "EMPTY" && (
            <>The replay ran but produced no rows after the label join.</>
          )}
        </div>
        <div className="text-[9px] text-[var(--muted-text)] italic mt-2">
          Historical paper replay only — not a live trading result.
        </div>
      </section>
    );
  }

  // Tone helpers — purely cosmetic; the underlying numbers are what they are.
  const hitAllTone: "green" | "amber" | "red" | "neutral" =
    data.hit_rate_all === null ? "neutral"
    : data.hit_rate_all >= 0.5 ? "green"
    : data.hit_rate_all >= 0.3 ? "amber"
    : "red";
  const pnlTone = (v: number | null | undefined): "green" | "red" | "neutral" =>
    v === null || v === undefined ? "neutral" : v >= 0 ? "green" : "red";

  return (
    <section
      data-testid="signal-backtest-summary"
      data-state="ok"
      data-backtest-date={data.date}
      data-candidate-count={data.candidate_count}
      data-labeled-count={data.labeled_count}
      className="rounded-2xl border border-[var(--card-border)] bg-[var(--card-bg)] p-4"
    >
      <header className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 text-sm font-bold tracking-wider uppercase text-[var(--app-text)]">
          <BarChart3 className="h-4 w-4 text-emerald-500" /> Backtest Summary · phase12-backtest · {data.date}
        </div>
        <span className={badgeClasses(data.status === "OK" ? "green" : "amber")}>
          {data.status} · {data.labeled_count} / {data.candidate_count} labeled
        </span>
      </header>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-3">
        <MetricTile label="HIT RATE (all)" value={formatPct(data.hit_rate_all)} tone={hitAllTone} />
        <MetricTile label="HIT RATE (top 3)" value={formatPct(data.hit_rate_top_3)} />
        <MetricTile label="HIT RATE (top 5)" value={formatPct(data.hit_rate_top_5)} />
        <MetricTile
          label="AVG SIM P&L (all)"
          value={formatPnlPct(data.average_simulated_net_pnl_pct_all)}
          tone={pnlTone(data.average_simulated_net_pnl_pct_all)}
        />
        <MetricTile
          label="AVG SIM P&L (top 3)"
          value={formatPnlPct(data.average_simulated_net_pnl_pct_top_3)}
          tone={pnlTone(data.average_simulated_net_pnl_pct_top_3)}
        />
        <MetricTile
          label="MEDIAN SIM P&L"
          value={formatPnlPct(data.median_simulated_net_pnl_pct)}
          tone={pnlTone(data.median_simulated_net_pnl_pct)}
        />
      </div>

      <div className="mb-3">
        <div className="text-[10px] uppercase tracking-wider text-[var(--muted-text)] font-bold mb-2">
          Label distribution ({data.labeled_count} labeled)
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-2">
          {[
            { key: "tradable_runner_count", short: "Tradable runner", tone: "green" as const },
            { key: "raw_runner_untradable_count", short: "Raw runner", tone: "amber" as const },
            { key: "fade_after_open_count", short: "Fade", tone: "neutral" as const },
            { key: "opening_drive_then_dump_count", short: "Drive→dump", tone: "red" as const },
            { key: "halt_to_zero_count", short: "Halt→zero", tone: "red" as const },
            { key: "offering_kill_count", short: "Offering kill", tone: "red" as const },
            { key: "no_trade_liquidity_count", short: "No trade · liquidity", tone: "neutral" as const },
            { key: "no_trade_spread_count", short: "No trade · spread", tone: "neutral" as const },
            { key: "no_trade_data_unverified_count", short: "Data unverified", tone: "neutral" as const },
          ].map(({ key, short, tone }) => {
            const n = (data as unknown as Record<string, number>)[key] || 0;
            return (
              <div
                key={key}
                className="rounded border border-[var(--card-border)] p-2 text-center"
                data-backtest-label={key}
              >
                <div className="text-[8px] uppercase tracking-wider text-[var(--muted-text)] font-bold leading-tight">
                  {short}
                </div>
                <div
                  className={`text-base font-black font-mono mt-1 ${
                    n > 0 && tone === "green" ? "text-emerald-500"
                    : n > 0 && tone === "red" ? "text-red-500"
                    : n > 0 && tone === "amber" ? "text-amber-500"
                    : n > 0 ? "text-[var(--app-text)]"
                    : "text-[var(--muted-text)]"
                  }`}
                >
                  {n}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-[11px] text-[var(--muted-text)]">
        {data.best_symbol && (
          <span data-testid="backtest-best">
            Best symbol:{" "}
            <span className="font-mono font-bold text-emerald-500">{data.best_symbol}</span>
          </span>
        )}
        {data.worst_symbol && (
          <span data-testid="backtest-worst">
            Worst symbol:{" "}
            <span className="font-mono font-bold text-red-500">{data.worst_symbol}</span>
          </span>
        )}
      </div>

      {data.score_vs_outcome_notes && (
        <div className="text-[10px] text-[var(--muted-text)] mt-2 leading-relaxed">
          {data.score_vs_outcome_notes}
        </div>
      )}

      <div className="text-[10px] text-[var(--muted-text)] italic mt-3 border-t border-[var(--card-border)] pt-2">
        Historical paper replay only — not a live trading result. Deterministic rules, no ML, no forward prediction, no buy/sell/short controls.
      </div>
    </section>
  );
}
